import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { Region } from 'wavesurfer.js/dist/plugins/regions.js';
import { Marker } from '../../types/vidmastro';
import { cn } from '@/lib/utils';

// We'll create a minimal RegionsPlugin type or use what comes with wavesurfer regions if types are tricky
// But generally standard wavesurfer types work.
// I'll rely on the default export of RegionsPlugin working.

interface MarkerTimelineProps {
    audioUrl: string;
    markers: Marker[];
    currentTime: number;
    onTimeUpdate: (time: number) => void;
    onMarkerClick: (markerId: string) => void;
    className?: string;
    activeMarkerId?: string | null;
}

export function MarkerTimeline({
    audioUrl,
    markers,
    currentTime,
    onTimeUpdate,
    onMarkerClick,
    className,
    activeMarkerId
}: MarkerTimelineProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurfer = useRef<WaveSurfer | null>(null);
    const regions = useRef<RegionsPlugin | null>(null);
    const [isReady, setIsReady] = useState(false);

    // Initialize WaveSurfer
    useEffect(() => {
        if (!containerRef.current) return;

        regions.current = RegionsPlugin.create();

        wavesurfer.current = WaveSurfer.create({
            container: containerRef.current,
            waveColor: '#4b5563', // gray-600
            progressColor: '#3b82f6', // blue-500
            cursorColor: '#ef4444', // red-500
            barWidth: 2,
            barGap: 1,
            barRadius: 2,
            height: 80,
            normalize: true,
            minPxPerSec: 50,
            plugins: [regions.current],
            backend: 'MediaElement', // Better for large files
        });

        wavesurfer.current.load(audioUrl);

        wavesurfer.current.on('ready', () => {
            setIsReady(true);
        });

        wavesurfer.current.on('interaction', (newTime) => {
            onTimeUpdate(newTime);
        });

        // Sync time updates from wavesurfer (when playing)
        wavesurfer.current.on('timeupdate', (time) => {
            // Avoid infinite loop if update comes from props
            // We'll let parent control time usually, but this is good for playback
            // Actually parent usually controls time via <video> element updates
        });

        // Cleanup
        return () => {
            wavesurfer.current?.destroy();
        };
    }, [audioUrl]);

    // Sync markers to regions
    useEffect(() => {
        if (!isReady || !regions.current) return;

        // Clear existing regions
        regions.current.clearRegions();

        // Add regions for markers
        markers.forEach(marker => {
            const color = getMarkerColor(marker.type, marker.status);

            const region = regions.current?.addRegion({
                id: marker.id,
                start: marker.timestampStart,
                end: marker.timestampEnd,
                color: color,
                drag: false,
                resize: false, // For now fixed
            });

            region?.element?.addEventListener('click', () => {
                onMarkerClick(marker.id);
            });
        });
    }, [markers, isReady, onMarkerClick]);

    // Sync current time cursor
    useEffect(() => {
        if (wavesurfer.current && Math.abs(wavesurfer.current.getCurrentTime() - currentTime) > 0.5) {
            if (!wavesurfer.current.isPlaying()) {
                wavesurfer.current.setTime(currentTime);
            }
        }
    }, [currentTime]);

    // Highlight active marker
    useEffect(() => {
        if (!isReady || !regions.current) return;

        // Maybe change opacity or border of active region?
        // WaveSurfer regions API is limited for dynamic style updates without re-creating
        // But we can try finding the element
        const allRegions = regions.current.getRegions();
        allRegions.forEach(r => {
            if (r.id === activeMarkerId) {
                r.setOptions({
                    color: getMarkerColor(
                        markers.find(m => m.id === r.id)?.type || 'manual',
                        'pending', // Status doesn't change color here, but can if needed
                        true // is active
                    )
                });
            } else {
                const m = markers.find(mark => mark.id === r.id);
                if (m) {
                    r.setOptions({ color: getMarkerColor(m.type, m.status, false) });
                }
            }
        });

    }, [activeMarkerId, isReady, markers]);

    return (
        <div className={cn("relative w-full bg-background/50 rounded-lg p-2", className)}>
            <div ref={containerRef} className="w-full" />
            {!isReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                    <p className="text-sm text-muted-foreground animate-pulse">Loading waveform...</p>
                </div>
            )}
        </div>
    );
}

function getMarkerColor(type: string, status: string, isActive = false): string {
    // Use RGBA for transparency
    const alpha = isActive ? 0.6 : 0.3;

    if (status === 'rejected') return `rgba(156, 163, 175, ${alpha})`; // Gray
    if (status === 'approved') return `rgba(34, 197, 94, ${alpha})`; // Green

    switch (type) {
        case 'voice_command': return `rgba(239, 68, 68, ${alpha})`; // Red
        case 'pause': return `rgba(234, 179, 8, ${alpha})`; // Yellow
        case 'filler': return `rgba(59, 130, 246, ${alpha})`; // Blue
        case 'retake': return `rgba(168, 85, 247, ${alpha})`; // Purple
        default: return `rgba(107, 114, 128, ${alpha})`;
    }
}
